import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Gift, Flame, Check, Lock, Star } from "lucide-react";
import { hapticSuccess } from "../utils/haptics";
import { shouldPromptReview, triggerInAppReview, recordReviewPrompt } from "../utils/inAppReview";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STREAK_DAY_LABELS = ["Day 1", "Day 2", "Day 3", "Day 4", "Day 5", "Day 6", "Day 7"];

export default function DailyReward() {
  const { user, dbUser, getAuthHeader } = useAuth();
  const [status, setStatus] = useState(null);
  const [collecting, setCollecting] = useState(false);
  const [justCollected, setJustCollected] = useState(false);
  const [pointsEarned, setPointsEarned] = useState(0);

  const fetchStatus = useCallback(async () => {
    if (!user) return;
    try {
      const token = await user.getIdToken();
      const res = await axios.get(`${API}/daily-reward/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStatus(res.data);
    } catch (e) {
      console.error("Failed to fetch daily reward status:", e);
    }
  }, [user]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const collectReward = async () => {
    if (!user || collecting) return;
    setCollecting(true);
    try {
      const token = await user.getIdToken();
      const res = await axios.post(`${API}/daily-reward/collect`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.data.already_collected_today) {
        setPointsEarned(res.data.points_earned);
        setJustCollected(true);
        hapticSuccess();
        setTimeout(() => setJustCollected(false), 3000);
        
        // Check if we should prompt for review (after streak reaches 3)
        const newStreak = res.data.new_streak || status?.streak;
        if (newStreak >= 3 && shouldPromptReview(dbUser)) {
          // Delay review prompt to not interrupt celebration
          setTimeout(async () => {
            const shown = await triggerInAppReview();
            if (shown) {
              await recordReviewPrompt(API, getAuthHeader());
            }
          }, 2000);
        }
      }
      fetchStatus();
    } catch (e) {
      console.error("Failed to collect daily reward:", e);
    } finally {
      setCollecting(false);
    }
  };

  if (!user || !status) return null;
  if (status.collected_today && !justCollected) {
    return (
      <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-4" data-testid="daily-reward-collected">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
            <Check className="w-5 h-5 text-emerald-400" />
          </div>
          <div className="flex-1">
            <p className="text-sm text-slate-300">Daily reward collected!</p>
            <p className="text-xs text-slate-500">Come back tomorrow. Streak: {status.streak} days</p>
          </div>
          <div className="flex items-center gap-1 text-amber-400">
            <Flame className="w-4 h-4" />
            <span className="text-sm font-bold">{status.streak}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden rounded-xl border border-amber-500/30 bg-gradient-to-br from-amber-900/30 to-orange-900/20" data-testid="daily-reward-available">
      {/* Celebration overlay */}
      {justCollected && (
        <div className="absolute inset-0 bg-emerald-500/10 flex items-center justify-center z-10 animate-pulse" data-testid="daily-reward-celebration">
          <div className="text-center">
            <p className="text-2xl font-bold text-emerald-400">+{pointsEarned} pts!</p>
            <p className="text-sm text-emerald-300">Streak: {status.streak} days</p>
          </div>
        </div>
      )}

      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Gift className="w-5 h-5 text-amber-400" />
            <h3 className="text-white font-semibold text-sm">Daily Reward</h3>
          </div>
          {status.streak > 0 && (
            <div className="flex items-center gap-1 text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded-full">
              <Flame className="w-3 h-3" />
              <span className="text-xs font-bold">{status.streak} day streak</span>
            </div>
          )}
        </div>

        {/* Streak day indicators */}
        <div className="flex gap-1.5 mb-3">
          {status.streak_rewards?.map((sr, i) => {
            const isCollected = sr.unlocked;
            const isCurrent = sr.current;
            const isLocked = !isCollected && !isCurrent;
            
            return (
              <div
                key={i}
                className={`flex-1 rounded-lg py-1.5 text-center text-[10px] font-medium transition-all ${
                  isCollected
                    ? "bg-emerald-500/20 border border-emerald-500/40 text-emerald-400"
                    : isCurrent
                    ? "bg-amber-500/20 border-2 border-amber-500/60 text-amber-300 ring-2 ring-amber-500/30 ring-offset-1 ring-offset-slate-900"
                    : "bg-slate-800/60 border border-slate-700/30 text-slate-500"
                }`}
                data-testid={`streak-day-${i + 1}`}
              >
                {isCollected ? (
                  <div className="flex flex-col items-center">
                    <Check className="w-3.5 h-3.5 text-emerald-400" />
                  </div>
                ) : isCurrent ? (
                  <div className="flex flex-col items-center animate-pulse">
                    <Gift className="w-3.5 h-3.5 text-amber-400" />
                  </div>
                ) : (
                  <div className="flex flex-col items-center">
                    <Lock className="w-3 h-3 text-slate-500" />
                  </div>
                )}
                <span className="block mt-0.5">{STREAK_DAY_LABELS[i]}</span>
              </div>
            );
          })}
        </div>

        <button
          onClick={collectReward}
          disabled={collecting || justCollected}
          data-testid="collect-daily-reward-btn"
          className="w-full py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold text-sm hover:from-amber-400 hover:to-orange-400 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {collecting ? (
            "Collecting..."
          ) : (
            <>
              <Gift className="w-4 h-4" />
              Collect +{status.points_if_collected} pts
            </>
          )}
        </button>
      </div>
    </div>
  );
}
